/**
 * Automation Runner Context
 * 
 * Provides centralized automation execution state and event emission.
 * Wraps the runner engine and exposes it to the React tree.
 * Stores automation timeline events for job-level visibility.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { DEFAULT_AUTOMATION_RULES, type AutomationRule, type AutomationExecution } from "./automationEngine";
import { type AutomationLog, runAutomation, detectJobStateEvents, type AutomationRunResult } from "./automationRunner";
import type { SystemEvent, CommunicationEvent } from "./communicationEngine";
import type { Job } from "./types";

interface AutomationRunnerContextType {
  rules: AutomationRule[];
  executions: AutomationExecution[];
  logs: AutomationLog[];
  /** Automation-generated timeline events for job communication timelines */
  automationTimelineEvents: CommunicationEvent[];
  emitEvent: (event: SystemEvent, job?: Job) => void;
  detectAndEmit: (prevJobs: Job[], currentJobs: Job[]) => void;
  toggleRule: (ruleId: string) => void;
}

const AutomationRunnerContext = createContext<AutomationRunnerContextType | null>(null);

export function AutomationRunnerProvider({ children }: { children: React.ReactNode }) {
  const [rules, setRules] = useState<AutomationRule[]>(DEFAULT_AUTOMATION_RULES);
  const [executions, setExecutions] = useState<AutomationExecution[]>([]);
  const [logs, setLogs] = useState<AutomationLog[]>([]);
  const [automationTimelineEvents, setAutomationTimelineEvents] = useState<CommunicationEvent[]>([]);
  const rulesRef = useRef(rules);
  rulesRef.current = rules;

  const emitEvent = useCallback((event: SystemEvent, job?: Job) => {
    const result: AutomationRunResult = runAutomation(event, rulesRef.current, job);
    if (result.executions.length > 0) {
      setRules(result.updatedRules);
      setExecutions(prev => [...result.executions, ...prev].slice(0, 200));
      setLogs(prev => [...result.logs, ...prev].slice(0, 500));
      if (result.timelineEvents.length > 0) {
        setAutomationTimelineEvents(prev => [...result.timelineEvents, ...prev].slice(0, 500));
      }
    }
  }, []);

  const detectAndEmit = useCallback((prevJobs: Job[], currentJobs: Job[]) => {
    currentJobs.forEach(current => {
      const prev = prevJobs.find(p => p.id === current.id);
      if (!prev) return;
      const events = detectJobStateEvents(prev, current);
      events.forEach(evt => emitEvent(evt, current));
    });
  }, [emitEvent]);

  const toggleRule = useCallback((ruleId: string) => {
    setRules(prev => prev.map(r => r.id === ruleId ? { ...r, enabled: !r.enabled } : r));
  }, []);

  return (
    <AutomationRunnerContext.Provider value={{
      rules, executions, logs, automationTimelineEvents,
      emitEvent, detectAndEmit, toggleRule
    }}>
      {children}
    </AutomationRunnerContext.Provider>
  );
}

export function useAutomationRunner() {
  const ctx = useContext(AutomationRunnerContext);
  if (!ctx) throw new Error("useAutomationRunner must be inside AutomationRunnerProvider");
  return ctx;
}
